import React, { MouseEventHandler, ReactEventHandler } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { store } from '../../store'
import {
  signInSchema,
  forgotPasswordSchema,
  adminAccountChangePasswordSchema,
  emailInputSchema,
  projectSchema,
  homeSchema,
  opportunitySchema,
} from '../utils/schema'
import { AccountStatus } from './propsTypes_v1'
import { AssetsClass, PublishingStatus } from './opportunityTypes'
import { BlogCategory, BlogStatus } from '../../pages/blogs-form/constants'
import { WebinarStatus } from '../../pages/webinars-form/constants'
export interface IMessage {
  type: 'success' | 'error' | 'warning' | 'progress'
  data: string
}
export interface InputProps {
  type?: 'text' | 'date' | 'email' | 'file' | 'hidden' | 'number' | 'password' | 'range' | 'search'
  labelText?: string
  name: string
  value: string
  max?: number
  maxLength?: number
  min?: number
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onWheel?: (e: React.WheelEvent<HTMLInputElement>) => void
  required?: boolean
  placeholder?: string
  disabled?: boolean
  message?: IMessage
  errors?: { [key: string]: string | null }
  touched?: { [key: string]: boolean }
  dirty?: boolean
  infoText?: string
  classes?: string[]
  dataTestId?: string
}

export interface TextAreaProps {
  labelText?: string
  name: string
  value: string
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void
  required?: boolean
  placeholder?: string
  message?: IMessage
  errors?: { [key: string]: string | null }
  touched?: { [key: string]: boolean }
  infoText?: string
  dirty?: boolean
  dataTestId?: string
}

export interface DropdownOption {
  label: string
  value: string
}

export interface DropdownProps {
  labelText?: string
  name: string
  value: string
  handleChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void
  required?: boolean
  disabled?: boolean
  options?: DropdownOption[]
  message?: IMessage
  errors?: { [key: string]: string | null }
  touched?: { [key: string]: boolean }
  infoText?: string
  dirty?: boolean
  classes?: string[]
  dataTestId?: string
}

export interface IStatusProps {
  type: 'success' | 'error' | 'warning' | 'progress' | 'info'
  text: string | null
  variant: 'basic' | 'bold' | 'thin'
  IconComponent?: React.ReactNode
  dataTestId?: string
}

export interface IconProps {
  type?:
    | 'error'
    | 'success'
    | 'progress'
    | 'warning'
    | 'default'
    | 'warning-banner'
    | 'error-banner'
    | 'success-banner'
    | 'red'
    | 'default-light'
  height?: string
  onClick?: MouseEventHandler<SVGSVGElement>
  width?: string
  classes?: string[]
  dataTestId?: string
}

export interface ButtonProps {
  text?: string
  handleClick: () => void
  variant: 'primary-btn' | 'secondary-btn' | 'basic-btn'
  isDisabled?: boolean
  buttonType?: 'button' | 'submit' | 'reset' | undefined
  IconComponent?: React.ReactNode
  iconVariant?: 'left' | 'right'
  classes?: string[]
  dataTestId?: string
}
export interface AnchorProps {
  text: string
  href: string
  variant: 'basic-anchor' | 'label-anchor' | 'nav-link' | 'nav-link-bold'
  isDisabled?: boolean
  IconComponent?: React.ReactNode
  classes?: string[]
}
export interface IHeadingProps {
  text: string
  styled?: object
  classes?: string[]
}

export interface ICardProps {
  children?: React.ReactNode
  styled?: object
  classes?: string[]
  dataTestId?: string
}
export interface LoginDetails {
  email: string
  password: string
  rememberMe: string
}

export interface ILoginForm<T> {
  reRef?: React.RefObject<ReCAPTCHA>
  initialValues: T
  validationSchema: typeof signInSchema
  googleLoginHandler?: (value: string) => void
  handleFormSubmit: (value: LoginDetails) => void
}
export interface IFormProps<T1, T2> {
  reRef?: React.RefObject<ReCAPTCHA>
  initialValues: T1
  handleFormSubmit: (value: T2) => void
  validationSchema:
    | typeof signInSchema
    | typeof forgotPasswordSchema
    | typeof adminAccountChangePasswordSchema
    | typeof emailInputSchema
    | typeof projectSchema
    | typeof homeSchema
    | typeof opportunitySchema
  googleLoginHandler?: (value: object) => void
  handleFileUpload?: (value: File, type: 'image' | 'logo' | 'doc' | 'video') => any
  handleErrorMessage?: (value: string) => void
}
export interface CheckboxProps {
  labelText?: string
  name: string
  value?: string
  checked?: boolean
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  errors?: { [key: string]: string | null }
  touched?: { [key: string]: boolean }
  dirty?: boolean
  dataTestId?: string
}

export interface PProps {
  text: string
  variant?: 'center' | 'left' | 'right' | 'default-gray' | 'default-blue' | 'default-red'
  textSize?: 'regular' | 'small'
  classes?: string[]
}

export interface SpanProps {
  text: string
  children?: React.ReactNode
  variant?: 'default' | 'bold' | 'thin' | 'large'
  classes?: string[]
  dataTestId?: string
}
export interface FlexProps {
  children?: React.ReactNode
  variant?:
    | 'column-center'
    | 'row-center'
    | 'space-between'
    | 'column'
    | 'row'
    | 'space-evenly'
    | 'flex-end'
    | 'align-center'
  classes?: string[]
  onClick?: ReactEventHandler
  dataTestId?: string
}

export interface gbcUserState {
  authToken: string
  email: string
  userId: number
  role: string
  isEmailVerified: boolean
  permissions?: Record<string, string>
  actionGrants?: Record<string, boolean>
}

export enum Role {
  superUser = 'SUPERUSER',
  investor = 'INVESTOR',
  gbcUser = 'GBC_USER',
}

export interface BannerState {
  show: boolean
  text: string
  variant?: 'warning-banner' | 'error-banner' | 'success-banner' | 'permission-denied-banner'
}

export type ShowBannerPayload = Omit<BannerState, 'show'>

export interface ModelState {
  name: string
  isShow: boolean
  isConfirm: boolean
  message: string
}

export type ShowModelPayload = Omit<ModelState, 'isShow' | 'isConfirm'>

export type AppDispatch = typeof store.dispatch
export type Event = React.ChangeEvent<HTMLInputElement>
export interface VerifyEmailProps {
  email: string
  handleClick: () => void
}

export interface VerifiedEmailProps {
  handleClick: () => void
}

export interface IGoogleReCaptchaProps {
  handleChange: (reCaptchaToken: string | null) => void
  handleExpired?: () => void
  reCaptchaRef?: React.RefObject<ReCAPTCHA>
  size?: 'normal' | 'compact' | 'invisible'
}

export interface list {
  text: string
  value: string
  disabled?: boolean
}
export interface SelectDropdownProps {
  classes?: string[]
  id?: string
  formId?: string
  name: string
  list: list[]
  handleChange?: (val: string) => void
  errors?: { [key: string]: string | null }
  touched?: { [key: string]: boolean }
  dirty?: boolean
  value?: string
  defaultValue?: string
  placeholder?: string
  required?: boolean
  dataTestId?: string
  isDisabled?: boolean
}

export interface InputFileProps {
  label: string
  name: string
  file?: FileDataProps
  type?: 'image' | 'logo' | 'doc' | 'video'
  errors?: { [key: string]: string | null }
  touched?: { [key: string]: boolean }
  dirty?: boolean
  handleFiles: (fileData: FileDataProps | undefined) => void
  handleDeleteFile?: () => void
  infoText?: string
  dataTestId?: string
}

export interface MultiInputFileProps {
  label: string
  name: string
  file?: FileDataProps[]
  type?: 'image' | 'doc' | 'csv'
  errors?: { [key: string]: string | null }
  touched?: { [key: string]: boolean }
  dirty?: boolean
  handleFiles: (fileData: FileDataProps[] | undefined) => void
  handleDeleteFile?: () => void
  dataTestId?: string
}

export interface FileDataProps {
  name: string
  size?: number
  type: string
  date?: Date
}
export interface ProgressBarProps {
  variant?: 'default-bar' | 'green-bar' | 'red-bar'
  value?: string
  maxValue?: string
}

export interface TagProps {
  variant?: 'white-bg' | 'black-bg' | 'gold-bg'
  text: string
  classes?: string[]
}

export interface ImageProps {
  source?: string
  classes?: string[]
  variant?: 'all' | 'top' | 'left' | 'right' | 'bottom' | 'round'
  radius?: 'r8' | 'r16'
}

export interface ListProps {
  listData?: string[]
  classes?: string[]
}

export interface CTABannerProps {
  classes?: string[]
  heading?: string
  subHeading?: string
  ButtonComponent?: React.ReactNode
}

export interface ProjectCardProps {
  id: number
  projectName: string
  projectSlug: string
  closingDate: string
  irr: string
  ticketSize: string
  projectSummary: string
  valuation: string
  companyWebsite: string
  location: string
  raiseGoal: string
  overallRaiseGoal: string
  dealContours: string[]
  companyDetails: string
  companyMilestones: string[]
  legalDocuments: string
  isHighCommitenceProject: boolean
  isHideCompanyDetails: boolean
  companyName: string
  projectCover: string
  assetClass: string
  isFeatured: boolean
  isPublished: boolean
  clientEngagement1: string
  clientEngagement2: string
  clientEngagement3: string
  clientEngagement4: string
  companyLogo: string
  isSavedForLater: boolean
  noOfInvestors: string
  committedAmount: string
  handleClick?: (projectSlug: string) => void
  createdDate?: string
  updatedAt?: string
  createdAt?: string
  currencyCode?: string
}

export interface FeaturedProjectProps {
  featuredProject: ProjectCardProps
  handleClick: (projectSlug: string) => void
}
export interface SearchParams {
  keyword?: string
  sortBy?:
    | 'NEWEST'
    | 'INVESTMENT_HIGH_TO_LOW'
    | 'INVESTMENT_LOW_TO_HIGH'
    | 'SIGNUP_NEWEST'
    | 'SIGNUP_OLDEST'
    | ''
  filterBy?: string
  status?: string
  closingFilter?: 'ACTIVE' | 'CLOSED'
  account_status?: string
  signupFrom?: string
  signupTo?: string
  offset: string
  limit: string
}

/** List row from GET /v1/opportunity-interest (amount in cents). */
export interface OpportunityInterestListItem {
  id: number
  investor_id: number
  opportunity_id: number
  amount: number
  created_at: string
  updated_at: string
  investor_reference?: string
  investor_email?: string
  first_name?: string
  last_name?: string
  opportunity_title?: string
  opportunity_slug?: string
  currency_code?: string
}

/** Core row from GET /v1/opportunity-interest/{id} (amount in cents). */
export interface OpportunityInterestDetail {
  id: number
  investor_id: number
  opportunity_id: number
  amount: number
  created_at: string
  updated_at: string
}

/** Known statuses for investment-limit increase requests. */
export type InvestmentLimitRequestStatus = 'SUBMITTED' | 'APPROVED' | 'REJECTED' | string

/** List row from GET /v1/kyc/investment-limit-requests (currentInvestmentLimit in cents). */
export interface InvestmentLimitRequestListItem {
  id: string
  investorId: number
  investorName: string
  investorEmail: string
  clientId: string
  currentInvestmentLimit: number
  requestedLimitRange: string
  requestedLimitRangeLabel: string
  status: InvestmentLimitRequestStatus
  documentCount: number
  reviewNote?: string | null
  reviewedAt?: string | null
  createdAt: string
  updatedAt: string
}

export interface InvestmentLimitRequestDocument {
  documentType: string
  documentTypeLabel: string
  fileName: string
  mimeType: string
  fileId: string
  s3Key: string
  downloadUrl: string
}

/** Detail from GET /v1/kyc/investment-limit-requests/{id}. */
export interface InvestmentLimitRequestDetail extends InvestmentLimitRequestListItem {
  portfolioId?: string
  documents: InvestmentLimitRequestDocument[]
  reviewedByAdminEmail?: string | null
}

export type InvestmentLimitRequestReviewPayload =
  | {
      status: 'APPROVED'
      investmentLimit: number
      note?: string
    }
  | {
      status: 'REJECTED'
      note?: string
    }

export interface InvestmentLimitRequestReviewResult {
  id: string
  status: InvestmentLimitRequestStatus
  reviewNote?: string | null
  reviewedAt?: string | null
  reviewedByAdminEmail?: string | null
  message?: string
}

export interface InvestmentLimitRequestSearchParams {
  status?: string
  investorId?: string | number
  limit?: number
  offset?: number
}

export interface InvestmentSearchParams {
  investor_id?: number | string
  opportunity_id?: number | string
  keyword?: string
  sortBy?:
    | 'CLOSING_DATE_DESC' // default
    | 'CLOSING_DATE_ASC'
    | 'TICKET_SIZE_DESC'
    | 'TICKET_SIZE_ASC'
    | ''
  filterBy?: string
  offset: string
  limit: string
}

export interface InvestorInvestmentSearchParams extends InvestmentSearchParams {
  investor_portfolio_id?: string
}

/** Stored risk profiler payload from GET /v1/user/{id}/profile (current, 15q, or legacy). */
export type InvestorRiskProfileAnswers = {
  answers?: Record<string, string | string[]>
  /** Current 6-answer (5 scored, 25–100) or previous 15-question (15–75) schema. */
  totalScore?: number
  /** Legacy 6-question schema. */
  averageScore?: number
  /** Current/15q: low | moderate_low | … ; Legacy: conservative | moderate | … */
  riskBand?: string | null
} | null

export interface InvestorProps {
  createdAt?: string
  updatedAt?: string
  userId: number
  acceptedKYCAccuracy?: boolean
  acceptedTNC?: boolean
  accountStatus: AccountStatus
  acceptedNDA?: boolean
  investorType?: string
  kycStatus: string
  investmentLimit: number
  totalInvestedAmount?: number
  email: string
  role?: Role
  isEmailVerified?: boolean
  userSource?: string
  name: string
  countryOfResidence: string
  countryCode: number
  phoneNumber: number
  citizenship: string
  passportNumber: number
  kycId?: number
  investorReference?: string
  riskProfileCompleted?: boolean
  riskProfileAnswers?: InvestorRiskProfileAnswers
  metadata?: {
    referral_code?: string
    utm_attribution?: {
      utm_source?: string
      utm_medium?: string
      utm_campaign?: string
      utm_content?: string
      utm_term?: string
      captured_at?: string
      landing_path?: string
    }
  }
}

export interface InvestorData {
  investor: InvestorProps
}

export interface ToggleProps {
  checked: boolean
  classes?: string[]
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  isDisable?: boolean
  dataTestId?: string
}

export interface ToolTipProps {
  content: React.ReactNode
  variant?: 'table-cell'
  children: JSX.Element
  contentClassNames?: string[]
}
export interface ToolTipBlackProps {
  classes?: string[]
  variant: 'pending-icon' | 'info-icon' | 'check-circle-icon' | 'report-icon'
  children: JSX.Element | string
}

export interface Jobs {
  jobId: string
  createdAt: string
  fileId: string
  status: string
  email: string
  investmentId: number
}

export interface JobResult {
  email: string
  fileId: string
  status: string
  metadata: any
  reason: string
  investorId: number
}

export interface JobResponse {
  createdAt: string
  updatedAt: string
  jobId: string
  type: string
  payload: {
    projectId: number
    fileIds: string[]
  }
  status: string
  result?: {
    jobResults?: JobResult[]
  }
}

// v1

export const InvestorQueryCategoryType = [
  'Platform',
  'Account',
  'Investments',
  'Verification and KYC',
  'Refunds',
  'Urgent Request',
]

type AssistanceCategoryType = typeof InvestorQueryCategoryType
export interface Queries {
  id: number
  created_at: string
  updated_at: string
  name: string
  email: string
  subject: string
  message: string
  assistance_category?: AssistanceCategoryType
}

export interface OpportunityTerm {
  level1: {
    header: string
    termObjects: Array<{ title: string; content: string }>
  }
  level2: {
    header: string
    termObjects: Array<{ title: string; content: string }>
  }
}

export interface NewOpportunityUpdate {
  opportunity_id: number
  title: string
  description: string
}

export interface OpportunityUpdates extends NewOpportunityUpdate {
  id: string
  created_at: string
}

export type RedemptionPeriod = 'Monthly' | 'Quarterly' | 'Semi Annual' | 'Annual' | null
export type PayoutFrequency = 'Monthly' | 'Quarterly' | 'Semi Annual' | 'Annual' | null

// Asset class specific data interface - supports Trade Finance, Mutual Fund, and Listed Equity
export interface AssetClassData {
  payout_frequency?: PayoutFrequency
  investment_period_in_days?: number | null
  interest_charged_by_credibila_to_opportunity_percent?: number | null
  expected_spread_percent?: number | null
  days_from_withdraw_request_to_payment?: number | null
  exit_load_percent?: number | null
  profit_sharing_to_credibila_percent?: number | null
  hosting_fee_percent?: number | null
  // `has_lock_in_period` is stored as a boolean in the form; prefer explicit boolean
  // rather than allowing null to avoid TypeScript widening issues.
  has_lock_in_period?: boolean
  lock_in_period_in_days?: number | null
  management_fee_percent?: number | null
  total_shares?: number | null
  early_redemption_allowed?: boolean
  redemption_notice_in_days?: number | null
  redemption_fee_percent?: number | null
  redemption_period?: RedemptionPeriod
  redemption_gate_percent?: number | null
}

// Legacy type alias for backward compatibility
export type TradeFinanceData = AssetClassData

export interface NewOpportunity {
  [key: string]:
    | string
    | boolean
    | AssetsClass
    | PublishingStatus
    | Array<string>
    | number
    | OpportunityTerm
    | object
    | undefined
    | null
  opportunity_title: string
  opportunity_slug: string
  opportunity_domain?: string | null
  jurisdiction: string
  currency_code: string
  is_featured: boolean
  asset_class: AssetsClass
  sector_id?: number | null
  status: PublishingStatus
  images?: Array<string>
  video?: string
  opportunity_summary: string
  short_opportunity_summary: string
  closing_date: string | null
  investment_return: number
  ticket_size: number
  documents: {
    title: string
    url: string
  }[]
  key_factors?: Array<string>
  terms?: OpportunityTerm
  highlights?: Array<string>
  metadata: {
    company?: {
      website?: string
      about?: string
    }
    is_eligible_for_custom_terms?: boolean
    is_fixed_income?: boolean
    /** When true, client CTA shows "Request Allocation" instead of invest flow. */
    request_allocation_enabled?: boolean
    /** When true, the client opportunities page spotlights this opportunity (hero + highlighted card). */
    highlight_enabled?: boolean
    /** Price per share in USD (e.g. '9'). When set, the client invest flow shows the share calculator. */
    price_per_share?: string
    /** Upfront / sales fee percentage applied to the share investment amount (e.g. '5'). */
    sales_fee_percent?: string
    portfolio_service_fee?: string
    asset_fee?: string
    trail_commission?: string
    upfront_commission?: string
    hurdle_rate?: string
    carry_rate?: string
    stock_nse?: string
    stock_bse?: string
    underlying_fund?: string
    securities_offering?: string
    reference_valuation?: {
      currency: string
      amount: number | null
    } | null
    termsheet_agreement_template_url?: string
    termsheetFieldMapping?: Record<string, FieldMapping>
  }
  cover_image: string
  raise_goal?: number | null
  coupon_payment_interval_in_months?: number | null
  is_public?: boolean
  marketing_signup_enabled?: boolean
  marketing_signup_image?: string | null
  marketing_signup_mobile_image?: string | null
  logo_image?: string | null
  logo_wide_image?: string | null
  transfer_page_enabled?: boolean
  secondary_trading_enabled?: boolean
  access_token?: string | null
  asset_class_data?: AssetClassData | null
  minimum_return?: number
  maximum_return?: number
  /** Client listing order; lower values appear first. Null uses fallback. */
  display_order?: number | null
}

export interface Opportunity extends NewOpportunity {
  id: number
  no_of_investors: number
  total_investment_amount: number
  updates: Array<OpportunityUpdates>
}

export interface OpportunityCardProps {
  id: number
  opportunitySlug: string
  opportunityTitle: string
  assetClass: string
  expectedReturn: string
  ticketSize: string
  status: string
  committedPercentage: string
  closingDate: string | null
  displayOrder?: number | null
  canEditOrder?: boolean
  handleDisplayOrderSave?: (id: number, value: string) => void
  handleClick: (opportunityTitle: string, opportunitySlug: string, id: number) => void
  handleProjectInvestmentClick: (
    opportunityTitle: string,
    opportunitySlug: string,
    opportunity_id: number,
  ) => void
  handleProjectWithdrawalClick: (
    opportunityTitle: string,
    opportunitySlug: string,
    opportunity_id: number,
  ) => void
}

export interface LastMinuteListPropsV1 {
  opportunitySlug: string
  opportunityTitle: string
  committedPercentage: string
  opportunitySummary: string
  opportunityCover: string
  handleClick: (opportunity_slug: string) => void
  closingDate: string
}

export interface InvestmentSearchParamsV1 {
  userId?: number | string
  opportunity_id?: number | string
  keyword?: string
  sortBy?:
    | 'CLOSING_DATE_DESC' // default
    | 'CLOSING_DATE_ASC'
    | 'TICKET_SIZE_DESC'
    | 'TICKET_SIZE_ASC'
    | ''
  filterBy?: string
  offset: string
  limit: string
}

export interface InvestmentPropsV1 {
  id: number
  investor_id: number
  name: string
  email: string
  status: string
  amount: string
  opportunity_id: number
  opportunity_slug: string
  opportunity_title: string
  created_at: string
  currency_code: string
  asset_class: string
  closing_date: string
  ticket_size: number
}

export type FieldMapping = {
  source: string
}

export type SimplifiedFieldMapping = {
  source: string
  /** SignNow template field to fill, when it differs from the mapping key. */
  fieldName?: string
}

export type MappingRow = {
  id: string
  keyName: string
  map: SimplifiedFieldMapping
}

export type FormValues = {
  metadata?: {
    termsheetFieldMapping?: Record<string, SimplifiedFieldMapping>
  }
}

export type GetBlogsSearchParams = {
  keyword?: string
  sortBy?: 'NEWEST_TO_OLDEST' | 'OLDEST_TO_NEWEST'
  status?: BlogStatus
  category?: BlogCategory
  offset: string
  limit: string
}

export type GetWebinarsSearchParams = {
  keyword?: string
  sortBy?: 'NEWEST_TO_OLDEST' | 'OLDEST_TO_NEWEST'
  status?: WebinarStatus
  offset: string
  limit: string
}

export type GetNewsletterSubscribersSearchParams = {
  keyword?: string
  sortBy?: 'NEWEST_TO_OLDEST' | 'OLDEST_TO_NEWEST'
  offset: string
  limit: string
}

export type GetNewsletterGroupsSearchParams = {
  keyword?: string
  sortBy?: 'NEWEST_TO_OLDEST' | 'OLDEST_TO_NEWEST'
  offset: string
  limit: string
}
